/**
 * Presenter state: the active beat, the auto-tour countdown, freeze (with the
 * snapshot that unfreezing restores), the demo-data profile, the caption and
 * help visibility and the last beat that did not come up cleanly. Kept here
 * rather than in useUI so presenter mode stays self-contained; useUI only
 * owns the presenter flag that hides the chrome.
 *
 * The behaviour (beats, tour, freeze, demo profile) lives in session.ts; this
 * store only holds what the overlay renders and what other runtimes read.
 */
import { create } from 'zustand';
import type { FreezeSnapshot } from './controller';

/** A beat that timed out ('not-ready') or threw ('error'). */
export interface PresenterNote {
  beatIndex: number;
  kind: 'not-ready' | 'error';
  message: string;
  missing: string[];
  skipped: string[];
}

export interface PresenterState {
  /** Index into BEATS, or null before the first beat. */
  activeIndex: number | null;
  /** True while the active beat is still being set up. */
  applying: boolean;
  touring: boolean;
  /** Wall-clock ms when the tour moves on (null while a beat sets up, when paused or off). */
  tourDeadline: number | null;
  /** Dwell of the current tour step, for the countdown bar. */
  tourTotalMs: number | null;
  /** Remaining dwell kept while frozen. */
  tourRemainingMs: number | null;
  frozen: boolean;
  freezeSnapshot: FreezeSnapshot | null;
  demoProfile: boolean;
  captionVisible: boolean;
  helpOpen: boolean;
  lastError: PresenterNote | null;
  setHelpOpen: (open: boolean) => void;
}

export const INITIAL_PRESENTER_STATE = {
  activeIndex: null,
  applying: false,
  touring: false,
  tourDeadline: null,
  tourTotalMs: null,
  tourRemainingMs: null,
  frozen: false,
  freezeSnapshot: null,
  demoProfile: false,
  captionVisible: true,
  helpOpen: false,
  lastError: null,
} satisfies Omit<PresenterState, 'setHelpOpen'>;

export const usePresenter = create<PresenterState>()((set) => ({
  ...INITIAL_PRESENTER_STATE,
  setHelpOpen: (helpOpen) => set({ helpOpen }),
}));

/** True while presenter freeze holds the picture still (for the awareness and telemetry runtimes). */
export function isFrozen(): boolean {
  return usePresenter.getState().frozen;
}

/** True while the demo-data profile is on (for the loader and telemetry runtimes). */
export function isDemoProfile(): boolean {
  return usePresenter.getState().demoProfile;
}
